/**
 * 应用侧字体库（M2-18）。
 * 内置字体（io/fonts）加载进内存，按族名缓存 shaper（Face/Font 构建约
 * 10ms/次，每次排版都建就是卡顿）。
 *
 * 字体回退（用户反馈"选英文字体打中文变方框"后新增，见
 * `engine/text/fallback.ts`）：`shaperFor` 对外吐出的不是单一字体的
 * `TextShaper`，是自动挂了"缺字形就换一款已加载字体接着排"的组合
 * shaper——只有一款字体（或只请求那一款自己）时没有候选可回退，
 * 直接给回主字体本身，不套一层什么都不做的间接。
 */
import type { Shaper } from '../../engine/text/shaper';
import { TextShaper } from '../../engine/text/shaper';
import { FallbackTextShaper } from '../../engine/text/fallback';
import {
  BUILTIN_FONTS,
  loadBuiltinFonts,
  type FontAssetLoader,
  type FontRecord,
} from '../../io/fonts';

export class FontLibrary {
  #records: FontRecord[] = [];
  /** 每条记录自己的（不带回退）主 shaper，按记录对象本身缓存——同一条
   * 记录被多个族名的回退候选共用时，不用重复解析同一份字节。 */
  readonly #primaryShapers = new Map<FontRecord, TextShaper>();
  /** 对外可用的（可能带了回退候选）shaper，按调用方传入的族名字符串缓存。 */
  readonly #shaperCache = new Map<string, Shaper>();

  async loadBuiltins(loader: FontAssetLoader): Promise<void> {
    this.#records = await loadBuiltinFonts(loader);
  }

  /**
   * 追加一款动态来源的字体（用户导入/系统导入）。同族名后来者覆盖前者
   * （比如重新导入了同名字体的另一份文件）。任何一款字体的增删都可能
   * 改变**全部**族名的回退候选集合（新字体本身也是别人的候选），所以
   * `#shaperCache` 整体清空而不是只清这一个族名——粒度更细的失效在这里
   * 不值得，注册/移除字体是用户在对话框里的低频操作。
   */
  register(record: FontRecord): void {
    const index = this.#records.findIndex((r) => r.family === record.family);
    if (index >= 0) {
      this.#primaryShapers.delete(this.#records[index]!);
      this.#records[index] = record;
    } else {
      this.#records.push(record);
    }
    this.#shaperCache.clear();
  }

  /** 移除一款字体（"移除"按钮用）。找不到就是空操作。 */
  unregister(family: string): void {
    const index = this.#records.findIndex((r) => r.family === family);
    if (index < 0) return;
    this.#primaryShapers.delete(this.#records[index]!);
    this.#records.splice(index, 1);
    this.#shaperCache.clear();
  }

  /** 当前已加载的完整记录（字体管理对话框要按 `source` 分组展示）。 */
  get records(): readonly FontRecord[] {
    return this.#records;
  }

  /** 可选字体族（内置 + 将来的用户字体）。 */
  get families(): readonly string[] {
    return this.#records.map((r) => r.family);
  }

  get loaded(): boolean {
    return this.#records.length > 0;
  }

  /** 缺省字体族：CJK 覆盖最全的那款。 */
  get defaultFamily(): string {
    return this.#records[0]?.family ?? BUILTIN_FONTS[0]!.family;
  }

  #primaryShaperForRecord(record: FontRecord): TextShaper {
    const cached = this.#primaryShapers.get(record);
    if (cached !== undefined) return cached;
    const shaper = new TextShaper(record.bytes);
    this.#primaryShapers.set(record, shaper);
    return shaper;
  }

  shaperFor(family: string): Shaper | null {
    const cached = this.#shaperCache.get(family);
    if (cached !== undefined) return cached;
    const record = this.#records.find((r) => r.family === family) ?? this.#records[0];
    if (record === undefined) return null;
    const primary = this.#primaryShaperForRecord(record);
    const others = this.#records.filter((r) => r !== record);
    let result: Shaper;
    if (others.length === 0) {
      result = primary;
    } else {
      // 每个候选都是这次组合专用的独立实例，不进 #primaryShapers 共享
      // 缓存——下面要改它们的缩放基准（rescaleTo），共享实例被改了
      // 缩放基准会连累其它把它当自己主字体直接使用的地方
      const candidates = others.map((r) => {
        const shaper = new TextShaper(r.bytes);
        shaper.rescaleTo(primary.upem);
        return shaper;
      });
      result = new FallbackTextShaper(primary, candidates);
    }
    this.#shaperCache.set(family, result);
    return result;
  }
}

/** 浏览器资源加载器（public/ 下的路径）。 */
export function fetchFontLoader(baseUrl = '/'): FontAssetLoader {
  return async (path) => {
    const response = await fetch(`${baseUrl}${path}`);
    if (!response.ok) throw new Error(`字体资源 ${path} 加载失败：HTTP ${response.status}`);
    return new Uint8Array(await response.arrayBuffer());
  };
}
